"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/lib/auth";
import { apiFetch } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { usePlan } from "@/hooks/usePlan";
import { useOrgHref } from "@/hooks/useOrgHref";
import { usePersistTimeZone } from "@/hooks/usePersistTimeZone";
import AddonLauncher from "@/components/layout/AddonLauncher";
import { PLAN_COLORS, PLAN_DISPLAY_NAMES } from "@/lib/plans";
import type { PlanTier } from "@/lib/types";
import ChatWidget from "@/components/chat/ChatWidget";
import { FocusModeProvider, useFocusMode } from "@/contexts/FocusModeContext";
import { ChatPageStateProvider } from "@/contexts/ChatPageStateContext";
import { WorkspaceMenuSection } from "@/components/layout/WorkspaceSwitcher";
import FullScreenSpinner from "@/components/ui/FullScreenSpinner";
import { navItems, adminNavItems } from "./layout/navConfig";
import TrialBanner from "./layout/TrialBanner";
import ImpersonationBanner from "./layout/ImpersonationBanner";

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, isLoading, isAuthenticated, logout, getAccessToken, isImpersonating, impersonationTarget, exitImpersonation, activeOrg } = useAuth();
  const externalBilling = activeOrg?.billing_mode === "external";
  const { tier } = usePlan();
  const router = useRouter();
  const pathname = usePathname();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  usePersistTimeZone({ isAuthenticated, isImpersonating, getAccessToken });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Click-outside to close dropdown
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [dropdownOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsError, setTermsError] = useState("");

  const needsTerms = !isLoading && isAuthenticated && user && !user.terms_accepted_at;

  async function handleAcceptTerms() {
    if (!termsChecked) return;
    setTermsError("");
    setTermsLoading(true);
    try {
      const token = getAccessToken();
      await apiFetch("/auth/accept-terms", { method: "POST", token: token || undefined });
      window.sfAnalytics?.track("terms_accepted", "Auth");
      setTermsAccepted(true);
      // Hard refresh user state
      window.location.reload();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to accept terms.";
      setTermsError(msg);
    } finally {
      setTermsLoading(false);
    }
  }

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      router.push("/login");
    }
  }, [isLoading, isAuthenticated, router]);

  // Auto-save pending signature from public tool page after login/OAuth
  const pendingSigSaved = useRef(false);
  const { toast } = useToast();
  useEffect(() => {
    if (pendingSigSaved.current || !isAuthenticated) return;
    const pending = typeof window !== "undefined" ? localStorage.getItem("sf_pending_signature") : null;
    if (!pending) return;
    pendingSigSaved.current = true;
    try {
      const { image, sigName } = JSON.parse(pending);
      if (!image) return;
      localStorage.removeItem("sf_pending_signature");
      const accessToken = localStorage.getItem("sf_access_token");
      if (!accessToken) return;
      fetch("/api/auth/me/signatures", {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name: sigName || "My signature", image_base64: image }),
      }).then((res) => {
        if (res.ok) {
          toast("Signature saved to your account!", "success");
        }
      }).catch(() => {});
    } catch (_e) {
      localStorage.removeItem("sf_pending_signature");
    }
  }, [isAuthenticated, toast]);

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  if (!isAuthenticated) return null;

  const hidden = new Set<string>();
  if (externalBilling) hidden.add("Billing");
  if (activeOrg && !activeOrg.is_personal && activeOrg.role !== "owner") hidden.add("Billing");
  if (activeOrg?.is_personal) hidden.add("Members");
  const visibleNavItems = navItems.filter((i) => !hidden.has(i.name));
  const allNavItems = user?.role === "superadmin"
    ? [...visibleNavItems, ...adminNavItems]
    : visibleNavItems;

  return (
    <ChatPageStateProvider>
    <FocusModeProvider>
    <DashboardInner
      user={user}
      tier={tier}
      pathname={pathname}
      mobileMenuOpen={mobileMenuOpen}
      setMobileMenuOpen={setMobileMenuOpen}
      dropdownOpen={dropdownOpen}
      setDropdownOpen={setDropdownOpen}
      dropdownRef={dropdownRef}
      allNavItems={allNavItems}
      logout={logout}
      needsTerms={needsTerms}
      termsAccepted={termsAccepted}
      termsChecked={termsChecked}
      setTermsChecked={setTermsChecked}
      termsLoading={termsLoading}
      termsError={termsError}
      handleAcceptTerms={handleAcceptTerms}
      isImpersonating={isImpersonating}
      impersonationTarget={impersonationTarget}
      exitImpersonation={exitImpersonation}
      externalBilling={externalBilling}
      isPersonalOrg={activeOrg?.is_personal ?? false}
    >
      {children}
    </DashboardInner>
    </FocusModeProvider>
    </ChatPageStateProvider>
  );
}

function DashboardInner({
  user,
  tier,
  pathname,
  mobileMenuOpen,
  setMobileMenuOpen,
  dropdownOpen,
  setDropdownOpen,
  dropdownRef,
  allNavItems,
  logout,
  needsTerms,
  termsAccepted,
  termsChecked,
  setTermsChecked,
  termsLoading,
  termsError,
  handleAcceptTerms,
  isImpersonating,
  impersonationTarget,
  exitImpersonation,
  externalBilling,
  isPersonalOrg,
  children,
}: {
  user: { name?: string; email: string; role?: string; email_verified?: boolean; tier?: string; trial_expires_at?: string | null; blog_role?: string | null } | null;
  tier: PlanTier;
  pathname: string;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (v: boolean) => void;
  dropdownOpen: boolean;
  setDropdownOpen: (v: boolean) => void;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  allNavItems: { name: string; href: string; icon: React.ReactNode; external?: boolean }[];
  logout: () => void;
  needsTerms: boolean | "" | null | undefined;
  termsAccepted: boolean;
  termsChecked: boolean;
  setTermsChecked: (v: boolean) => void;
  termsLoading: boolean;
  termsError: string;
  handleAcceptTerms: () => void;
  isImpersonating: boolean;
  impersonationTarget: { name: string; email: string } | null;
  exitImpersonation: () => void;
  externalBilling: boolean;
  isPersonalOrg: boolean;
  children: React.ReactNode;
}) {
  const { sidebarCollapsed, headerHidden, toggleSidebar, exitFocusMode } = useFocusMode();
  const orgHref = useOrgHref();
  const { activeOrg } = useAuth();
  // Secondary label next to the name: the workspace for a real (non-personal)
  // org, else the plan — a personal workspace has no distinct org to show, so
  // surface the subscription tier there instead of leaving it blank.
  const secondaryLabel = activeOrg && !activeOrg.is_personal
    ? activeOrg.name
    : (PLAN_DISPLAY_NAMES[tier] || "Free");
  const sidebarHidden = new Set<string>();
  if (externalBilling) sidebarHidden.add("Billing");
  if (activeOrg && !activeOrg.is_personal && activeOrg.role !== "owner") sidebarHidden.add("Billing");
  if (isPersonalOrg) sidebarHidden.add("Members");
  const sidebarNavItems = navItems.filter((i) => !sidebarHidden.has(i.name));

  // Auto-exit focus mode when navigating away from focus-eligible pages
  useEffect(() => {
    if (!headerHidden) return;
    const focusPaths = [
      /\/envelopes\/[^/]+$/, // envelope detail: /envelopes/{id}
      /\/envelopes\/[^/]+\/edit$/, // envelope edit: /envelopes/{id}/edit
      /\/envelopes\/[^/]+\/sign$/, // envelope sign: /envelopes/{id}/sign
      /\/dashboard\/templates\/[^/]+$/, // template detail: /dashboard/templates/{id}
      /\/dashboard\/templates\/[^/]+\/edit$/, // template edit: /dashboard/templates/{id}/edit
      /\/envelopes\/new/, // new envelope: /envelopes/new/*
    ];
    const isFocusPage = focusPaths.some(re => re.test(pathname));
    if (!isFocusPage) exitFocusMode();
  }, [pathname, headerHidden, exitFocusMode]);

  // Keyboard shortcut: Ctrl+B to toggle sidebar
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === "b") {
        e.preventDefault();
        toggleSidebar();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  const sidebarWidth = sidebarCollapsed ? 64 : 244;

  return (
    <div className="flex min-h-screen bg-paper">
      {/* ─── Desktop Sidebar ─── */}
      <aside
        className="hidden md:flex flex-col border-r border-slate-200/80 bg-white fixed inset-y-0 left-0 z-30 transition-all duration-200"
        style={{ width: sidebarWidth }}
      >
        {/* Logo + collapse toggle */}
        {sidebarCollapsed ? (
          <div className="flex h-14 items-center justify-center border-b border-slate-100 px-2">
            <Link href={orgHref("/dashboard")}>
              <img src="/favicon.svg" alt="SignForge" className="w-8 h-8" />
            </Link>
          </div>
        ) : (
          <div className="flex h-14 items-center justify-between border-b border-slate-100 px-5">
            <Link href={orgHref("/dashboard")}>
              <h1 className="font-display text-lg font-bold text-ink tracking-tight">
                Sign<span className="text-brand-600">Forge</span>
              </h1>
            </Link>
            <button
              onClick={toggleSidebar}
              title="Collapse sidebar (Ctrl+B)"
              className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5" />
              </svg>
            </button>
          </div>
        )}

        {/* New Envelope CTA — prominent, top of sidebar
            (workspace switcher moved to the top bar to free vertical rail space) */}
        <div className="px-2 pt-1 pb-2">
          {sidebarCollapsed ? (
            <Link
              href={orgHref("/envelopes/new")}
              data-amanda-id="nav-new-envelope"
              title="New envelope"
              className="flex items-center justify-center w-10 h-10 mx-auto rounded-lg bg-[var(--color-gold)] text-[var(--color-ink)] shadow-sm hover:bg-[var(--color-gold-light)] active:scale-[0.98] transition-all duration-200 hover:shadow-md"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </Link>
          ) : (
            <Link
              href={orgHref("/envelopes/new")}
              data-amanda-id="nav-new-envelope"
              className="flex items-center justify-center gap-2 w-full rounded-lg bg-[var(--color-gold)] px-4 py-2.5 text-sm font-semibold text-[var(--color-ink)] shadow-sm hover:bg-[var(--color-gold-light)] active:scale-[0.98] transition-all duration-200 hover:shadow-md"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              New envelope
            </Link>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto sf-scroll-subtle">
          {sidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              title="Expand sidebar (Ctrl+B)"
              className="flex items-center justify-center w-full rounded-lg px-0 py-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors mb-1"
            >
              <svg className="h-4 w-4 rotate-180" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5" />
              </svg>
            </button>
          )}
          {sidebarNavItems.map((item) => {
            const prefixedHref = orgHref(item.href);
            const isActive = !item.external && (pathname === prefixedHref || pathname.startsWith(prefixedHref + "/"));
            return (
              <Link
                key={item.name}
                href={prefixedHref}
                data-amanda-id={`nav-${item.key}`}
                title={sidebarCollapsed ? item.name : undefined}
                {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className={`flex items-center rounded-lg text-[13px] font-medium transition-all duration-150 ${
                  sidebarCollapsed ? "justify-center px-0 py-2" : "gap-3 px-3 py-2"
                } ${
                  isActive
                    ? "bg-brand-50 text-brand-700 shadow-sm shadow-brand-100/50"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <span className={`shrink-0 ${isActive ? "text-brand-600" : "text-slate-400"}`}>{item.icon}</span>
                {!sidebarCollapsed && item.name}
                {!sidebarCollapsed && item.external && (
                  <svg className="h-3 w-3 text-slate-400 ml-auto" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                )}
              </Link>
            );
          })}

          {/* Entitlement-driven add-on launchers — SSO hand-off per addon */}
          <AddonLauncher collapsed={sidebarCollapsed} />

          {/* Admin section — admin only */}
          {user?.role === "superadmin" && (
            <>
              {!sidebarCollapsed && (
                <div className="pt-4 pb-1.5 px-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">
                    Admin
                  </p>
                </div>
              )}
              {sidebarCollapsed && <div className="pt-3 mb-1 border-t border-slate-100" />}
              {adminNavItems.map((item) => {
                const isActive = pathname === item.href || pathname.startsWith(item.href + "/");
                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    title={sidebarCollapsed ? item.name : undefined}
                    className={`flex items-center rounded-lg text-[13px] font-medium transition-all duration-150 ${
                      sidebarCollapsed ? "justify-center px-0 py-2" : "gap-3 px-3 py-2"
                    } ${
                      isActive
                        ? "bg-brand-50 text-brand-700 shadow-sm shadow-brand-100/50"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                  >
                    <span className={`shrink-0 ${isActive ? "text-brand-600" : "text-slate-400"}`}>{item.icon}</span>
                    {!sidebarCollapsed && item.name}
                  </Link>
                );
              })}
            </>
          )}

          {/* Blog studio — a FLAT platform route (never org-prefixed), shown to anyone
              with a blog role (spec 2026-10-29-blog §8). */}
          {user?.blog_role && (
            <>
              {!sidebarCollapsed && (
                <div className="pt-4 pb-1.5 px-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">
                    Content
                  </p>
                </div>
              )}
              {sidebarCollapsed && <div className="pt-3 mb-1 border-t border-slate-100" />}
              {(() => {
                const isActive = pathname === "/blog-studio" || pathname.startsWith("/blog-studio/");
                return (
                  <Link
                    href="/blog-studio"
                    title={sidebarCollapsed ? "Blog" : undefined}
                    className={`flex items-center rounded-lg text-[13px] font-medium transition-all duration-150 ${
                      sidebarCollapsed ? "justify-center px-0 py-2" : "gap-3 px-3 py-2"
                    } ${
                      isActive
                        ? "bg-brand-50 text-brand-700 shadow-sm shadow-brand-100/50"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                  >
                    <span className={`shrink-0 ${isActive ? "text-brand-600" : "text-slate-400"}`}><svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg></span>
                    {!sidebarCollapsed && "Blog"}
                  </Link>
                );
              })()}
            </>
          )}
        </nav>

        {/* Collapse toggle button */}
        <div className="px-2 py-1">
          <button
            onClick={toggleSidebar}
            title={sidebarCollapsed ? "Expand sidebar (Ctrl+B)" : "Collapse sidebar (Ctrl+B)"}
            className="flex items-center justify-center w-full rounded-lg px-2 py-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <svg
              className={`h-4 w-4 transition-transform duration-200 ${sidebarCollapsed ? "rotate-180" : ""}`}
              fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5" />
            </svg>
          </button>
        </div>

      </aside>

      {/* ─── Mobile Drawer Overlay ─── */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Drawer */}
          <div className="absolute inset-y-0 left-0 w-72 bg-white shadow-2xl flex flex-col" style={{ animation: "slideDrawerIn 0.25s ease-out" }}>
            {/* Drawer header */}
            <div className="flex h-14 items-center justify-between px-5 border-b border-slate-100">
              <Link href={orgHref("/dashboard")} onClick={() => setMobileMenuOpen(false)}>
                <h1 className="font-display text-lg font-bold text-ink tracking-tight">
                  Sign<span className="text-brand-600">Forge</span>
                </h1>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* New Envelope CTA */}
            <div className="px-3 pt-4 pb-2">
              <Link
                href={orgHref("/envelopes/new")}
                data-amanda-id="nav-new-envelope"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full rounded-lg bg-[var(--color-gold)] px-4 py-2.5 text-sm font-semibold text-[var(--color-ink)] shadow-sm hover:bg-[var(--color-gold-light)] transition-all"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                New envelope
              </Link>
            </div>

            {/* Nav items */}
            <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto sf-scroll-subtle">
              {allNavItems.map((item) => {
                const ext = "external" in item && item.external;
                const isAdmin = "href" in item && item.href.startsWith("/admin");
                const prefixedHref = isAdmin ? item.href : orgHref(item.href);
                const isActive = !ext && (pathname === prefixedHref || pathname.startsWith(prefixedHref + "/"));
                return (
                  <Link
                    key={item.name}
                    href={prefixedHref}
                    data-amanda-id={"key" in item ? `nav-${item.key}` : undefined}
                    onClick={() => setMobileMenuOpen(false)}
                    {...(ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-brand-50 text-brand-700"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                  >
                    <span className={isActive ? "text-brand-600" : "text-slate-400"}>{item.icon}</span>
                    {item.name}
                    {ext && (
                      <svg className="h-3 w-3 text-slate-400 ml-auto" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                    )}
                  </Link>
                );
              })}

              {/* Add-on launchers (Utilities, CLM) — the phone drawer is a separate
                  menu from the desktop sidebar and used to leave them out, so no
                  one could reach an add-on from a phone (2026-09-28). */}
              <AddonLauncher />

              {/* Blog studio — flat route, same rule as the desktop sidebar */}
              {user?.blog_role && (
                <Link
                  href="/blog-studio"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    pathname.startsWith("/blog-studio")
                      ? "bg-brand-50 text-brand-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <span className={pathname.startsWith("/blog-studio") ? "text-brand-600" : "text-slate-400"}><svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg></span>
                  Blog
                </Link>
              )}
            </nav>

            {/* User info + sign out */}
            <div className="border-t border-slate-100 p-3">
              <div className="flex items-center gap-3 px-2 py-2 mb-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-xs font-bold text-white shrink-0">
                  {user?.name?.charAt(0).toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                    <span className={`inline-flex items-center rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-wider shrink-0 ${PLAN_COLORS[tier]?.badge || PLAN_COLORS.free.badge}`}>
                      {PLAN_DISPLAY_NAMES[tier] || "Free"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={() => { setMobileMenuOpen(false); logout(); }}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                </svg>
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Main area ─── */}
      <div className={`sf-main-content flex flex-1 flex-col min-w-0 transition-all duration-200 ${headerHidden ? "h-screen max-h-screen" : ""}`}>
      {/* Dynamic sidebar margin for desktop */}
      <style>{`@media(min-width:768px){.sf-main-content{margin-left:${sidebarWidth}px!important;transition:margin-left 0.2s ease}}`}</style>
        {/* Header */}
        <header
          className={`sticky top-0 z-20 flex items-center justify-between border-b border-slate-200/80 bg-white/95 backdrop-blur-sm px-4 md:px-6 transition-all duration-200 ${
            headerHidden ? "md:h-0 md:overflow-hidden md:border-b-0 md:p-0" : "h-14"
          }`}
        >
          {/* Mobile: hamburger + logo */}
          <div className="flex items-center gap-3 md:hidden">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="rounded-lg p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label="Open menu"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
            <Link href={orgHref("/dashboard")}>
              <h1 className="font-display text-lg font-bold text-ink tracking-tight">
                Sign<span className="text-brand-600">Forge</span>
              </h1>
            </Link>
          </div>

          {/* Desktop: left slot (breadcrumb / page title could go here) */}
          <div className="hidden md:block" />

          {/* Right side actions */}
          <div className="flex items-center gap-3">
            <Link
              href={orgHref("/envelopes/new")}
              data-amanda-id="nav-new-envelope"
              className="md:hidden flex items-center gap-1.5 rounded-lg bg-[var(--color-gold)] px-3 py-1.5 text-xs font-semibold text-[var(--color-ink)] shadow-sm hover:bg-[var(--color-gold-light)] transition-all"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              New
            </Link>

            {/* Desktop: User dropdown */}
            <div className="hidden md:block relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 transition-colors"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-xs font-bold text-white shrink-0">
                  {user?.name?.charAt(0).toUpperCase() || "?"}
                </div>
                <div className="text-left flex items-baseline gap-1.5 min-w-0">
                  <p className="text-[13px] font-medium text-slate-900 leading-tight shrink-0">{user?.name}</p>
                  {secondaryLabel && (
                    <>
                      <span className="text-slate-300 shrink-0">·</span>
                      <span title={secondaryLabel} className="text-[12px] text-slate-500 leading-tight truncate max-w-[150px]">{secondaryLabel}</span>
                    </>
                  )}
                </div>
                <svg className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-64 rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50 z-50 animate-fade-in overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
                      <span className={`inline-flex items-center rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-wider shrink-0 ${PLAN_COLORS[tier]?.badge || PLAN_COLORS.free.badge}`}>
                        {PLAN_DISPLAY_NAMES[tier] || "Free"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                  </div>
                  {/* Workspaces — org switcher folded into the account menu (renders only when >1) */}
                  <WorkspaceMenuSection onNavigate={() => setDropdownOpen(false)} />
                  <div className="py-1">
                    <Link
                      href={orgHref("/settings")}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2 text-[13px] text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Settings
                    </Link>
                    {user?.role === "superadmin" && (
                      <Link
                        href="/admin"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-[13px] text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
                        </svg>
                        Admin Panel
                      </Link>
                    )}
                  </div>
                  <div className="border-t border-slate-100 py-1">
                    <button
                      onClick={() => { setDropdownOpen(false); logout(); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Impersonation banner */}
        <ImpersonationBanner
          isImpersonating={isImpersonating}
          impersonationTarget={impersonationTarget}
          exitImpersonation={exitImpersonation}
        />

        {/* Email verification banner */}
        {user && !user.email_verified && !isImpersonating && (
          <VerificationBanner email={user.email} />
        )}

        {/* Trial countdown banner */}
        <TrialBanner
          externalBilling={externalBilling}
          trialExpiresAt={user?.trial_expires_at}
          tier={user?.tier}
          billingHref={orgHref("/dashboard/billing")}
        />

        {/* Inline terms acceptance modal */}
        {needsTerms && !termsAccepted && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/50 backdrop-blur-sm">
            <div className="w-full max-w-lg mx-4 rounded-xl border border-slate-200 bg-white p-8 shadow-xl border-t-2 border-t-[var(--color-gold)] animate-fade-in">
              <h2 className="font-display text-xl font-bold text-ink tracking-tight">
                Terms of Service
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Please review and accept our Terms of Service to continue.
              </p>

              {termsError && (
                <div className="mt-4 rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
                  {termsError}
                </div>
              )}

              <div className="mt-6 max-h-56 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50/50 p-5 text-sm text-slate-600 leading-relaxed space-y-4">
                <p>By using SignForge, you agree to the following key terms:</p>
                <ul className="space-y-2 pl-4 list-disc marker:text-brand-400">
                  <li><strong className="text-slate-700">E-Signatures:</strong> Electronic signatures executed through SignForge are intended to be legally valid under the ESIGN Act, UETA, and IT Act 2000.</li>
                  <li><strong className="text-slate-700">No Legal Advice:</strong> SignForge is a technology platform, not a law firm. Consult qualified legal counsel for legal matters.</li>
                  <li><strong className="text-slate-700">Your Documents:</strong> You retain full ownership of all content. We do not use your documents to train AI models.</li>
                  <li><strong className="text-slate-700">Liability Cap:</strong> Our total liability is limited to the greater of $100 or 12 months of fees paid.</li>
                  <li><strong className="text-slate-700">Privacy:</strong> We collect data necessary to operate the Service. We do not sell your personal data.</li>
                </ul>
              </div>

              <label className="mt-6 flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={termsChecked}
                  onChange={(e) => setTermsChecked(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                />
                <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                  I have read and agree to the{" "}
                  <Link href="/terms" target="_blank" className="font-semibold text-brand-600 hover:text-brand-700 underline decoration-brand-200 underline-offset-2">
                    Terms of Service
                  </Link>
                </span>
              </label>

              <button
                onClick={handleAcceptTerms}
                disabled={!termsChecked || termsLoading}
                className="btn-primary w-full py-3 mt-6"
              >
                {termsLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                      <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
                    </svg>
                    Accepting...
                  </span>
                ) : (
                  "Continue to SignForge"
                )}
              </button>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className={`flex-1 sf-scroll-subtle ${headerHidden ? "overflow-y-auto pt-2 px-3 md:pt-3 md:px-4" : "p-4 md:p-6"}`}>
          {children}
        </main>
      </div>

      {/* Floating widget — AI chat (handles support + feedback) */}
      <ChatWidget />

      {/* Drawer animation keyframe (injected once) */}
      <style jsx>{`
        @keyframes slideDrawerIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

function VerificationBanner({ email }: { email: string }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleResend() {
    if (sending) return;
    setSending(true);
    try {
      await apiFetch("/auth/resend-verification", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setSent(true);
    } catch {
      // Silently fail
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 md:px-6 py-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <svg className="h-4 w-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <p className="text-sm text-amber-800">
            <strong>Verify your email</strong> to start sending documents for signature.
            {sent && <span className="ml-2 text-green-700">Verification email sent!</span>}
          </p>
        </div>
        {!sent && (
          <button
            onClick={handleResend}
            disabled={sending}
            className="shrink-0 rounded-md bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-200 transition-colors disabled:opacity-50"
          >
            {sending ? "Sending..." : "Resend email"}
          </button>
        )}
      </div>
    </div>
  );
}
